import { useCallback, useRef, useState } from 'react';

import { ProductsSequenceProduct } from 'common/contracts';

import { appendProduct, replaceWithSingleProduct } from './usePopupProducts.utils';

export type ProductEntry = ProductsSequenceProduct & { id: string };

interface IUsePopupProductsReturn {
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  setProducts: React.Dispatch<React.SetStateAction<ProductEntry[]>>;
}

/**
 * Manages a product list for popup forms.
 *
 * @example
 * const { productsList, addProduct } = usePopupProducts(true);
 */
export const usePopupProducts = (isSingleProductMode: boolean): IUsePopupProductsReturn => {
  const [products, setProducts] = useState<ProductEntry[]>([]);
  const prevSingleMode = useRef(isSingleProductMode);

  if (isSingleProductMode && !prevSingleMode.current && products.length > 1) {
    setProducts(products.slice(0, 1));
  }
  prevSingleMode.current = isSingleProductMode;

  const addProduct = useCallback(
    (newProduct: ProductsSequenceProduct): void => {
      if (isSingleProductMode) {
        setProducts(replaceWithSingleProduct(newProduct));
      } else {
        setProducts((prev) => appendProduct(prev, newProduct));
      }
    },
    [isSingleProductMode]
  );

  const deleteProduct = useCallback(
    (id: string): void => {
      setProducts((prev) => prev.filter((p) => p.id !== id));
    },
    []
  );

  const updateProduct = useCallback(
    (id: string, newProductData: ProductsSequenceProduct): void => {
      setProducts((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, ...newProductData } : p
        )
      );
    },
    []
  );

  return {
    productsList: products,
    addProduct,
    deleteProduct,
    updateProduct,
    setProducts
  };
};
